
import { useEffect, useState } from 'react'
import './style.scss'
import { userStateData } from '../../data/userStateData'
import { useAppSelector } from '../../redux/hooks'
import LabeledInput from '../../components/labeledInput/LabeledInput'
import LabeledDropDown from '../../components/labeledDropDown/LabeledDropDown'
import { INSTRUCTOR_GENDERS, INSTRUCTOR_SCHOOLS, LANGUAGES } from '../../constants/searchConstants'
import { ImageDocumentResponse, ImageUploadResponse } from '../../server/types'
import { API_URL, DOCUMENT_FILE_PATH, PROFILE_PIC_URL } from '../../constants/pathConstants'
import { updateConnector } from '../../server/connector/updateConnector'
import CtaButton from '../../components/ctaButton/CtaButton'
import { useCookies } from 'react-cookie'
import { TERRAINS } from '../../constants/searchConstants'
import { AccountStatus } from '../../types/types'
import LabeledTextArea from '../../components/labeledTextArea/LabeledTextArea'
import { Orientation, getOrientation } from 'get-orientation'
import Cropper from 'react-easy-crop'
import { defaultProfilePicFileName } from '../../constants/instructorConstants'
import { getCroppedImg, getRotatedImage, readFile } from '../../utils/Cropper'

const BANK_COUNTRIES: string[] = ['UK', 'New Zealand']
const NUMBER_CHARS: string[] = ['0','1','2','3','4','5','6','7','8','9']

function DashboardInstructorProfileContentArea() {
	const [userDataLoaded, setUserDataLoaded] = useState<boolean>(false)

	const dataLoadRequestFinished: boolean = useAppSelector(state => state.user.dataLoadRequestFinished)

	const [profilePicUrls, setProfilePicUrls] = useState<string[] | undefined>(undefined)
	const [yourName, setYourName] = useState<string>('')
	const [accountStatus, setAccountStatus] = useState<string>()
	const [country, setCountry] = useState<string>('')
	const [phoneNumber, setPhoneNumber] = useState<string>('')
	const [groupSize, setGroupSize] = useState<string>('')
	const [gender, setGender] = useState<string | undefined>(undefined)
	const [level, setLevel] = useState<string | undefined>(undefined)
	const [aboutText, setAboutText] = useState<string | undefined>(undefined)
	const [dobDate, setDobDate] = useState<string | undefined>(undefined)
	const [dobMonth, setDobMonth] = useState<string | undefined>(undefined)
	const [dobYear, setDobYear] = useState<number | undefined>(undefined)

	// ========================================================= Minimum requirement checks
	const [minimumRequirementsMet, setMinimumRequirementsMet] = useState<boolean>(false)
	const minimumAboutWordCount: number = 20
	const [meetPhotoRequirements, setMeetPhotoRequirements] = useState<boolean>(false)
	const [meetLanguageRequirements, setMeetLanguageRequirements] = useState<boolean>(false)
	const [meetIdRequirements, setMeetIdRequirements] = useState<boolean>(false)
	const [meetAboutTextRequirements, setMeetAboutTextRequirements] = useState<boolean>(false)
	const [meetBankRequirements, setMeetBankRequirements] = useState<boolean>(false)


	const [price, setPrice] = useState<string | undefined>(undefined)
	// const [price2, setPrice2] = useState<string | undefined>(undefined)
	// const [price3, setPrice3] = useState<string | undefined>(undefined)
	// const [price4, setPrice4] = useState<string | undefined>(undefined)

	const [skiSchool, setSkiSchool] = useState<string | undefined>(undefined)
	const [languages, setLanguages] = useState<string | undefined>(undefined)
	const [terrains, setTerrains] = useState<string | undefined>(undefined)

	const [idFilePath, setIdFilePath] = useState<string | undefined>(undefined)
	const [certificateFilePath, setCertificateFilePath] = useState<string | undefined>(undefined)

	const [bankName, setBankName] = useState<string>('')
	const [account, setAccount] = useState<string>('')
	const [sortCode1, setSortCode1] = useState<string>('')
	const [sortCode2, setSortCode2] = useState<string>('')
	const [sortCode3, setSortCode3] = useState<string>('')
	const [swiftBic, setSwiftBic] = useState<string>('')

	const [bankCountry, setBankCountry] = useState<string | undefined>(undefined)

	const [uploadingImage, setUploadingImage] = useState<boolean>(false)
	const [uploadingDocument, setUploadingDocument] = useState<boolean>(false)
	const [updatingUserData, setUpdatingUserData] = useState<boolean>(false)

	const [valueChanged, setValueChanged] = useState<boolean>(false) // flag indicating if values have changed to disable update button

	const [cookies, setCookie] = useCookies(['userData'])

	const MIN_PRICE: number = 0
	// const MAX_PRICE: number = 40
	// const MAX_PRICE_2: number = 55
	// const MAX_PRICE_3: number = 70
	// const MAX_PRICE_4: number = 85

	const MAX_PRICES: number[] = [0, 40, 55, 70, 85]

	// Setup test values
	useEffect(() => {
		if (dataLoadRequestFinished === true) {
			updateUserValues()
			setUserDataLoaded(true)
		}
	}, [dataLoadRequestFinished])

	useEffect(() => {
		// Called to trigger update of minimum data requirement text using state data
		if (userDataLoaded === true) {
			checkMinimumRequirements()
		}
	}, [userDataLoaded])

	// Update the local user cookies to values match when browser is refreshed without loading from server
	const updateCookies = () => {
		setCookie("userData", userStateData.getUserData(), {
			path: "/"
		})
	}

	const checkMinimumRequirements = () => {
		// Used on data load and file updates
		setMeetPhotoRequirements(hasFirstPhoto())
		setMeetLanguageRequirements(hasLanguage())
		setMeetIdRequirements(hasId())
		setMeetAboutTextRequirements(hasEnoughAboutText())
		setMeetBankRequirements(hasBankDetails())

		setMinimumRequirementsMet(
			hasFirstPhoto() &&
			hasLanguage() && 
			hasId() && 
			hasEnoughAboutText() &&
			hasBankDetails()
		)
	}
	const hasFirstPhoto = (): boolean => {
		return profilePicUrls != undefined && profilePicUrls.length > 0 && profilePicUrls[0] != defaultProfilePicFileName
	}
	const hasLanguage = (): boolean => {
		return languages != undefined && languages.length > 0
	}
	const hasEnoughAboutText = (): boolean => {
		return aboutText != undefined && aboutText.split(' ').length > minimumAboutWordCount - 1
	}
	const hasId = (): boolean => {
		return idFilePath != undefined && idFilePath.length > 0
	}
	const hasBankDetails = () => {
		let hasBankData: boolean = false
		if (bankCountry) {
			if (bankCountry === BANK_COUNTRIES[0]) {
				// UK
				if (account.length > 0 && sortCode1.length == 2 && sortCode2.length == 2 && sortCode3.length == 2) {
					hasBankData = true
				}
			} else if (bankCountry === BANK_COUNTRIES[1]) {
				// New Zeland
				if (account.length > 0 && swiftBic.length > 0) {
					hasBankData = true
				}
			}
		}
		return hasBankData
	}

	const updateUserValues = () => {
		setYourName(userStateData.getUsersName())
		setCountry(userStateData.getCountry())
		setAccountStatus(userStateData.getStatus())

		setPhoneNumber(userStateData.getPhoneNumber())
		setGender(userStateData.getGender())
		setLevel(userStateData.getLevel())
		setAboutText(userStateData.getAboutText())
		setLanguages(userStateData.getLanguages())
		setSkiSchool(userStateData.getSkiSchool())
		setTerrains(userStateData.getTerrains())
		setPrice(userStateData.getPrice())
		// setPrice2(userStateData.getPrice_2())
		// setPrice3(userStateData.getPrice_3())
		// setPrice4(userStateData.getPrice_4())
		// setGroupSize(userStateData.getGroupSize())

		const currentDob: string | undefined = userStateData.getDob()
		if (currentDob) {
			const dob: Date = new Date(currentDob)
			setDobDate(dob.getDate() < 10 ? `0${dob.getDate()}` : `${dob.getDate()}`)
			// setDobMonth(dob.getMonth() + 1)
			setDobMonth(dob.getMonth() + 1 < 10 ? `0${dob.getMonth() + 1}` : `${dob.getMonth() + 1}`)
			setDobYear(dob.getFullYear())
			// setDobDate(currentDob.split('-')[0])
			// // setDobMonth(dob.getMonth() + 1)
			// setDobMonth(currentDob.split('-')[1])
			// setDobYear(Number(currentDob.split('-')[2]))
		}

		setBankCountry(userStateData.getBankCountry())
		setBankName(userStateData.getBankName())
		setAccount(userStateData.getBankAccountNumber())
		const sortCodeStr: string | undefined = userStateData.getBankSortCode()
		if (sortCodeStr) {
			const splitCodes: string[] = sortCodeStr.split('-')
			if (splitCodes.length === 3) {
				setSortCode1(splitCodes[0])
				setSortCode2(splitCodes[1])
				setSortCode3(splitCodes[2])
			}
		}
		setSwiftBic(userStateData.getBankSwiftBic())

		setCertificateFilePath(userStateData.getCertificateFilePath())
		setIdFilePath(userStateData.getIdFilePath())
		setProfilePicUrls(userStateData.getSplitProfilePics())
	}

	const yourNameOnChange = (newValue: string) => {
		setYourName(newValue)
		setValueChanged(true)
	}
	const phoneNumberOnChange = (newValue: string) => {
		setPhoneNumber(newValue)
		setValueChanged(true)
	}
	const countryOnChange = (newValue: string) => {
		setCountry(newValue)
		setValueChanged(true)
	}
	const genderOnChange = (newValue: string) => {
		setGender(newValue)
		setValueChanged(true)
	}
	const schoolOnChange = (newValue: string) => {
		setSkiSchool(newValue)
		setValueChanged(true)
	}

	const languageOnChange = (newValue: string) => {
		let indexOfValue: string = `${LANGUAGES.indexOf(newValue)}`
		let languagesArr: string[] = []
		let removedLanguage: boolean = false
		if (languages) {
			const currentLangsArr: string[] = languages?.split(',')
			currentLangsArr.forEach((langId: string) => {
				if (langId === indexOfValue) {
					removedLanguage = true
				} else {
					languagesArr.push(langId)
				}
			})
		}
		if (removedLanguage === false) {
			languagesArr.push(indexOfValue)
		}
		setLanguages(languagesArr.join(','))

		setMeetLanguageRequirements(languagesArr.length > 0)
		setMinimumRequirementsMet(
			hasFirstPhoto() &&
			languagesArr.length > 0 && 
			hasId() && 
			hasEnoughAboutText() &&
			hasBankDetails()
		)

		setValueChanged(true)
	}

	const groupsSizeOnChange = (newValue: string) => {
		setGroupSize(newValue)
		setValueChanged(true)
	}

	const terrainsOnChange = (newValue: string) => {
		let indexOfValue: string = `${TERRAINS.indexOf(newValue)}`
		let terrainsArr: string[] = []
		let removedTerrain: boolean = false
		if (terrains) {
			const currentTerrainArr: string[] = terrains?.split(',')
			currentTerrainArr.forEach((id: string) => {
				if (id === indexOfValue) {
					removedTerrain = true
				} else {
					terrainsArr.push(id)
				}
			})
		}
		if (removedTerrain === false) {
			terrainsArr.push(indexOfValue)
		}
		setTerrains(terrainsArr.join(','))
		setValueChanged(true)
	}

	const levelOnChange = (newValue: string) => {
		setLevel(newValue)
		// userStateData.setStatus(AccountStatus.ACTIVE)
		// setAccountStatus(AccountStatus.ACTIVE)
		setValueChanged(true)
	}

	const bankCountryOnChange = (newValue: string) => {
		setBankCountry(newValue)
		setValueChanged(true)
	}

	// const qualificationsOnChange = (newValue: string) => {
	// 	setQualifications(newValue)
	// }

	const aboutTextOnChange = (newValue: string) => {
		setAboutText(newValue)
		const aboutTextLongEnough: boolean = newValue.split(' ').length > minimumAboutWordCount - 1
		setMeetAboutTextRequirements(aboutTextLongEnough)

		// Check if minimum requirements are met, need to move into seperate function but for now duplicate single call because react state values not updating during render cycle
		setMinimumRequirementsMet(
			hasFirstPhoto() &&
			hasLanguage() && 
			hasId() && 
			aboutTextLongEnough &&
			hasBankDetails()
		)
		setValueChanged(true)
	}

	// ================================================ DOB STUFF ===========================================
	const onDobDateChange = (newValue: string) => {
		// setDobDate(parseInt(newValue))
		setDobDate(newValue)
		setValueChanged(true)
	}
	const onDobMonthChange = (newValue: string) => {
		// setDobMonth(parseInt(newValue))
		setDobMonth(newValue)
		setValueChanged(true)
	}
	const onDobYearChange = (newValue: string) => {
		setDobYear(parseInt(newValue))
		setValueChanged(true)
	}

	// ================================================ BANK ================================================

	const bankNameOnChange = (newValue: string) => {
		setBankName(newValue)
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const accountOnChange = (newValue: string) => {
		console.log('newValue, ', newValue)
		const splitValues: string[] = newValue.split('')
		let formedString: string = ''
		splitValues.forEach((char: string) => {
			if (NUMBER_CHARS.includes(char)) {
				formedString = `${formedString}${char}`
			}
		})
		setAccount(formedString)
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const sortCode1Change = (newValue: string) => {
		// if (newValue.length > 2) {
		// 	setSortCode1(`${newValue[0]}${newValue[1]}`)
		// } else {
		// 	setSortCode1(parseInt(newValue).toString())
		// }
		const splitValues: string[] = newValue.split('')
		let formedString: string = ''
		splitValues.forEach((char: string) => {
			if (!Number.isNaN(parseInt(char))) {
				if (formedString.length < 2) {
					formedString = formedString + char
				}
			}
		})
		setSortCode1(formedString)
		// if (Number.isNaN(parseInt(newValue))) {
		// 	setSortCode1('')
		// } else {
		// 	// setSortCode1(parseInt(newValue).toString())
		// 	if (newValue.length > 2) {
		// 		setSortCode1(`${newValue[0]}${newValue[1]}`)
		// 	} else {
		// 		setSortCode1(parseInt(newValue).toString())
		// 	}
		// }
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const sortCode2Change = (newValue: string) => {
		// setSortCode2(newValue.substring(0, 2))
		// if (Number.isNaN(parseInt(newValue))) {
		// 	setSortCode2('')
		// } else {
		// 	// setSortCode1(parseInt(newValue).toString())
		// 	if (newValue.length > 2) {
		// 		setSortCode2(`${newValue[0]}${newValue[1]}`)
		// 	} else {
		// 		setSortCode2(parseInt(newValue).toString())
		// 	}
		// }
		const splitValues: string[] = newValue.split('')
		let formedString: string = ''
		splitValues.forEach((char: string) => {
			if (!Number.isNaN(parseInt(char))) {
				if (formedString.length < 2) {
					formedString = formedString + char
				}
			}
		})
		setSortCode2(formedString)
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const sortCode3Change = (newValue: string) => {
		// setSortCode3(newValue.substring(0, 2))
		// if (Number.isNaN(parseInt(newValue))) {
		// 	setSortCode3('')
		// } else {
		// 	// setSortCode1(parseInt(newValue).toString())
		// 	if (newValue.length > 2) {
		// 		setSortCode3(`${newValue[0]}${newValue[1]}`)
		// 	} else {
		// 		setSortCode3(parseInt(newValue).toString())
		// 	}
		// }
		const splitValues: string[] = newValue.split('')
		let formedString: string = ''
		splitValues.forEach((char: string) => {
			if (!Number.isNaN(parseInt(char))) {
				if (formedString.length < 2) {
					formedString = formedString + char
				}
			}
		})
		setSortCode3(formedString)
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const swiftBicOnChange = (newValue: string) => {
		setSwiftBic(newValue)
		checkMinimumRequirements()
		setValueChanged(true)
	}
	const onBankInputBlur = () => {
		checkMinimumRequirements()
	}

	const priceOnChange = (newValue: string) => {
		// if (Number(newValue) < MIN_PRICE) {
		// 	setPrice(MIN_PRICE.toFixed(2))	
		// } else if (Number(newValue) > MAX_PRICE) {
		// 	setPrice(MAX_PRICE.toFixed(2))
		// } else {
		// 	// setPrice(Number(newValue).toFixed(2))
		// 	setPrice(newValue)
		// }
		setPrice(newValue)
		setValueChanged(true)
	}
	const onPriceBlur = () => {
		if (price && level) {
			setPrice(getStringWithinRange(price, 0, MAX_PRICES[Number(level)]))
		}
	}
	// const price2OnChange = (newValue: string) => {
	// 	setPrice2(newValue)
	// 	setValueChanged(true)
	// }
	// const price3OnChange = (newValue: string) => {
	// 	setPrice3(newValue)
	// 	setValueChanged(true)
	// }
	// const price4OnChange = (newValue: string) => {
	// 	setPrice4(newValue)
	// 	setValueChanged(true)
	// }



	// ================================================ USER DATA ===========================================

	const setUserData = () => {
		userStateData.setName(yourName)
		userStateData.setCountry(country)
		userStateData.setPhoneNumber(phoneNumber)
		userStateData.setGender(gender)
		// userStateData.setGroupSize(groupSize)
		userStateData.setLanguages(languages)
		userStateData.setTerrains(terrains)
		userStateData.setSkiSchool(skiSchool)
		// userStateData.setQualifications(qualifications)
		// userStateData.setPrice(price)
		// if (Number(price) < MIN_PRICE) {
		// 	setPrice(MIN_PRICE.toFixed(2))
		// 	userStateData.setPrice(MIN_PRICE.toFixed(2))
		// } else if (Number(price) > MAX_PRICE) {
		// 	setPrice(MAX_PRICE.toFixed(2))
		// 	userStateData.setPrice(MAX_PRICE.toFixed(2))
		// } else {
		// 	userStateData.setPrice(Number(price).toFixed(2))
		// }
		if (price) {
			// userStateData.setPrice(getStringWithinRange(price, MIN_PRICE, MAX_PRICE))
			userStateData.setPrice(getStringWithinRange(price, 0, MAX_PRICES[Number(level)]))
		}
		// if (price2) {
		// 	userStateData.setPrice_2(getStringWithinRange(price2, MIN_PRICE, MAX_PRICE_2))
		// }
		// if (price3) {
		// 	userStateData.setPrice_3(getStringWithinRange(price3, MIN_PRICE, MAX_PRICE_3))
		// }
		// if (price4) {
		// 	userStateData.setPrice_4(getStringWithinRange(price4, MIN_PRICE, MAX_PRICE_4))
		// }

		if (dobDate && dobMonth && dobYear) {
			userStateData.setDob(parseInt(dobDate), parseInt(dobMonth) - 1, dobYear)
		}
		userStateData.setBankCountry(bankCountry)
		userStateData.setBankName(bankName)
		userStateData.setBankAccountNumber(account)
		if (sortCode1 && sortCode2 && sortCode3) {
			userStateData.setBankSortCode(`${sortCode1}-${sortCode2}-${sortCode3}`)
		} else {
			userStateData.setBankSortCode(undefined)
		}
		userStateData.setBankSwiftBic(swiftBic)
		userStateData.setAboutText(aboutText)
		if (level) {
			userStateData.setLevel(level)
		}
	}

	const getStringWithinRange = (value: string, min_value: number, max_value: number): string => {
		if (Number(value) < min_value) {
			return min_value.toFixed(2)
		} else if (Number(value) > max_value) {
			return max_value.toFixed(2)
		} else {
			return Number(value).toFixed(2)
		}
	}

	const uploadImage = (file: File, profilePicIndex: number) => {
		setUploadingImage(true)
		const API_ENDPOINT = `${API_URL}upload-image`
		const request = new XMLHttpRequest()
		const formData = new FormData()

		request.open("POST", API_ENDPOINT, true)
		request.onreadystatechange = () => {
			if (request.readyState === 4 && request.status === 200) {
				const responseObj: ImageUploadResponse = JSON.parse(request.responseText)
				if (responseObj.error) {
					setUploadingImage(false)
				} else if (responseObj.fileName) {
					imageAdded(responseObj.fileName, profilePicIndex)
				}
			}
		}
		formData.append("file", file);
		request.send(formData);
	}

	const uploadDocument = (file: File) => {
		setUploadingDocument(true)
		const API_ENDPOINT = `${API_URL}upload-document`
		const request = new XMLHttpRequest()
		const formData = new FormData()

		request.open("POST", API_ENDPOINT, true)
		request.onreadystatechange = () => {
			if (request.readyState === 4 && request.status === 200) {
				const responseObj: ImageDocumentResponse = JSON.parse(request.responseText)
				if (responseObj.error) {
					setUploadingDocument(false)
				} else if (responseObj.fileName) {
					userStateData.setCertificateFilePath(responseObj.fileName)
					userStateData.setStatus(AccountStatus.CERTIFICATE_UNDER_REVIEW)
					documentAdded() // responseObj.fileName)
				}
			}
		}
		formData.append("file", file);
		request.send(formData);
	}

	const uploadId = (file: File) => {
		setUploadingDocument(true)
		const API_ENDPOINT = `${API_URL}upload-document`
		const request = new XMLHttpRequest()
		const formData = new FormData()

		request.open("POST", API_ENDPOINT, true)
		request.onreadystatechange = () => {
			if (request.readyState === 4 && request.status === 200) {
				const responseObj: ImageDocumentResponse = JSON.parse(request.responseText)
				if (responseObj.error) {
					setUploadingDocument(false)
				} else if (responseObj.fileName) {
					// documentAdded(responseObj.fileName)
					// setUploadingDocument(false)
					// userStateData.setIdFilePath(responseObj.fileName)
					// setIdFilePath(userStateData.getIdFilePath())

					// setIdFilePath(userStateData.getIdFilePath())
					userStateData.setIdFilePath(responseObj.fileName)
					// userStateData.setStatus(AccountStatus.CERTIFICATE_UNDER_REVIEW)
					documentAdded() // responseObj.fileName)
				}
			}
		}
		formData.append("file", file);
		request.send(formData);
	}

	const imageAdded = (fileName: string, profilePicIndex: number) => {
		setUploadingImage(false)
		// Called when new image upload is complete so image details can be updated
		setUpdatingUserData(true)
		setValueChanged(true)
		// reform image string
		let splitPics: string[] = userStateData.getSplitProfilePics()
		if (splitPics[profilePicIndex]) {
			splitPics[profilePicIndex] = fileName
		}
		const newString: string = splitPics.join(',')

		if (updateConnector.requestInProgress) {
			console.log('[DashboardInstructorProfileContentArea] - imageAdded() called while requestInProgress')
		} else {
			updateConnector.updateInstructorProfilePicsRequest(userStateData.getId(), newString).then((successRequest) => {
				setUpdatingUserData(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardInstructorProfileContentArea] - imageAdded() failed')
				} else {
					userStateData.setProfilePics(newString)
					// updateUserValues()
					setProfilePicUrls(userStateData.getSplitProfilePics())
					updateCookies()
					// window.location.replace(PAGE_URLS.DASHBOARD_CUSTOMER)
				}
			}).finally(() => {
				checkMinimumRequirements()
			})
		}
	}

	const documentAdded = () => { // fileName: string) => {
		setUploadingDocument(false)
		// Called when new file upload is complete so file details can be updated
		setUpdatingUserData(true)
		setValueChanged(true)


		if (updateConnector.requestInProgress) {
			console.log('[DashboardInstructorProfileContentArea] - updateDataOnClick() called while requestInProgress')
		} else {
			updateConnector.updateInstructorDataRequest(userStateData.getId()).then((successRequest) => {
				setUpdatingUserData(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardInstructorProfileContentArea] - updateDataOnClick() failed')
				} else {
					// updateUserValues()
					setCertificateFilePath(userStateData.getCertificateFilePath())
					setIdFilePath(userStateData.getIdFilePath())
					updateCookies()
					// window.location.replace(PAGE_URLS.DASHBOARD_CUSTOMER)
				}
			}).finally(() => {
				checkMinimumRequirements()
			})
		}
	}

	const updateDataOnClick = () => {
		setUpdatingUserData(true)
		setValueChanged(false)

		setUserData()

		if (updateConnector.requestInProgress) {
			console.log('[DashboardInstructorProfileContentArea] - updateDataOnClick() called while requestInProgress')
		} else {
			if (userStateData.getStatus() != AccountStatus.CERTIFICATE_UNDER_REVIEW) {
				if (!minimumRequirementsMet) {
					userStateData.setStatus(AccountStatus.INCOMPLETE)
				}
			} else if (userStateData.getStatus() == AccountStatus.INCOMPLETE) {
				if (minimumRequirementsMet) {
					userStateData.setStatus(AccountStatus.ACTIVE)
				}
			}
			updateConnector.updateInstructorDataRequest(userStateData.getId()).then((successRequest) => {
				setUpdatingUserData(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardInstructorProfileContentArea] - updateDataOnClick() failed')
				} else {
					updateUserValues()
					updateCookies()
					// window.location.replace(PAGE_URLS.DASHBOARD_CUSTOMER)
				}
			})
		}
	}

	const toggleActiveState = () => {
		userStateData.toggleActiveState()
		setAccountStatus(userStateData.getStatus())
		setValueChanged(true)
	}

	// TODO: These functions are a mess and need to be moved into utils folders and cleaned up

	
	const [crop, setCrop] = useState({ x: 0, y: 0 })
	const [rotation, setRotation] = useState(0)
	const [zoom, setZoom] = useState(1)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
	const [imageIndex, setImageIndex] = useState<number>(0)
	const [imageSrc, setImageSrc] = useState<any>(null)
	const onFileChange = async (e: any) => {
		if (e.target.files && e.target.files.length > 0) {
			const file = e.target.files[0]
			let imageDataUrl = await readFile(file)

			try {
				// apply rotation if needed
				const orientation = await getOrientation(file)
				let rotation: number = 0 // ORIENTATION_TO_ANGLE[orientation]
				if (orientation == Orientation.BOTTOM_RIGHT) {
					rotation = 180
				} else if (orientation == Orientation.RIGHT_TOP) {
					rotation = 90
				} else if (orientation == Orientation.LEFT_BOTTOM) {
					rotation = -90
				}
				imageDataUrl = await getRotatedImage(imageDataUrl, rotation)
			} catch (e) {
				console.warn('failed to detect the orientation')
			}

			setImageSrc(imageDataUrl)
		}
	}
	const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
		setCroppedAreaPixels(croppedAreaPixels)
	}
	const showCroppedImage = async () => {
		try {
			const croppedImage = await getCroppedImg(
				imageSrc,
				croppedAreaPixels,
				rotation
			)
			uploadImage(croppedImage, imageIndex)
			setImageSrc(null)
		} catch (e) {
			console.error(e)
		}
	}

	return (
		<div className='dashboard-content-area-wrap'>
			{userDataLoaded &&
				<>
					<div className='dashboard-content-area-wide-card-wrap'>
						<div className='dashboard-profile-section-wrap'>
							{profilePicUrls && profilePicUrls.length > 0 &&
								<div className='dashboard-profile-instructor-profile-img-content'>
									{profilePicUrls.map((picUrl: string, index: number) =>
										<div
											className='dashboard-profile-profile-img-wrap'
											key={`profileImg${index}`}
										>
											<div className='dashboard-profile-instructor-profile-img-wrap'>
												<img
													className='dashboard-profile-instructor-profile-img'
													src={`${PROFILE_PIC_URL}${picUrl}`}
													alt='Profile'
												/>
											</div>
											<label
												className='dashboard-profile-profile-img-upload-button'
											>
												<input
													type='file'
													onChange={(e) => {
														setImageIndex(index)
														onFileChange(e)
													}}
													accept='image/*'
													disabled={uploadingImage || uploadingDocument || updatingUserData}
												/>
												<img
													src='/imgs/icons/dashboard/icon-edit.svg'
													alt='Change Profile'
												/>
											</label>
										</div>
									)}
								</div>
							}
						</div>
						<div className='dashboard-instructor-profile-section-wrap'>
							<div className='dashboard-instructor-profile-section-main-title'>
								Welcome {yourName}
							</div>
							<div className='dashboard-instructor-profile-section-main-sub-title'>
								{userStateData.getEmail()}
							</div>
							<div className='dashboard-instructor-profile-section-sub-title'>
								Please be sure to regularly check emails and in spam folder for notifications
							</div>
							{minimumRequirementsMet ?
								<div className='dashboard-instructor-profile-section-main-sub-title'>
									Status "{userStateData.getStatusMessage()}"
									{(accountStatus === AccountStatus.ACTIVE || accountStatus === AccountStatus.DISABLED) &&
										<label className="dashboard-instructor-profile-switch">
											<input
												type="checkbox"
												checked={accountStatus === AccountStatus.ACTIVE}
												onClick={() => {
													toggleActiveState()
												}}
											/>
											<span className="dashboard-instructor-profile-slider round"></span>
										</label>
									}
								</div>
								:
								<div className='dashboard-warning-message-txt'>
									Minimum requirements to activate account are not met, (*) indicate missing data
								</div>
							}
							{/* {level && */}
								<div className='dashboard-instructor-profile-section-level-wrap'>
									<div className={`dashboard-profile-instructor-level dashboard-profile-instructor-level-${level}`}>Level {level}</div>
								</div>
							{/* } */}
							<div className='dashboard-instructor-profile-section-title'>
								About You
							</div>
							<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-double'>
								<LabeledInput
									label='Your Name'
									onChange={yourNameOnChange}
									value={yourName}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
								<LabeledInput
									label='Phone Number'
									onChange={phoneNumberOnChange}
									value={phoneNumber}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
							</div>
							<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-double'>
								<LabeledInput
									label='Country of Residence'
									onChange={countryOnChange}
									value={country}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
								<LabeledDropDown
									label='Gender'
									onChange={genderOnChange}
									value={gender}
									options={INSTRUCTOR_GENDERS}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
							</div>
							<div className='dashboard-instructor-profile-section-sub-title'>
								Date of Birth
							</div>
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Date'
									options={Array.from({ length: 31 }, (_, i) => `${i + 1 < 10 ? `0${i + 1}` : i + 1}`)}
									onChange={onDobDateChange}
									value={`${dobDate}`}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
								<LabeledDropDown
									label='Month'
									options={Array.from({ length: 12 }, (_, i) => `${i + 1 < 10 ? `0${i + 1}` : i + 1}`)}
									onChange={onDobMonthChange}
									// value={dobMonth ? dobMonth < 10 ? `0${dobMonth}` : `${dobMonth}` : undefined}
									value={dobMonth}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
								<LabeledDropDown
									label='Year'
									options={Array.from({ length: 50 }, (_, i) => `${i + 2025 - 15 - 50}`)}
									onChange={onDobYearChange}
									value={`${dobYear}`}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
							</div>
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledTextArea
									label='About You Message'
									value={aboutText}
									onChange={aboutTextOnChange}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
							</div>
							{// aboutText && aboutText?.length > 0 && !meetAboutTextRequirements &&
							!meetAboutTextRequirements &&
								<div className='dashboard-warning-message-txt'>
									* About You Message requires at least {minimumAboutWordCount} words
								</div>
							}
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='School'
									onChange={schoolOnChange}
									value={skiSchool}
									options={INSTRUCTOR_SCHOOLS}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									blankValue='Add Language'
								/>
							</div>
							{/* <div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Group Size'
									onChange={groupsSizeOnChange}
									value={groupSize}
									options={['1,2,3,4', '1,2,3', '1,2', '1']}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									keepBlank={true}
									blankValue='Choose Group Size'
								/>
							</div> */}
							<div className='dashboard-instructor-profile-section-title'>
								Skills & Preferences
							</div>
							{uploadingDocument ?
								<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-margin'>
									<img
										src='/imgs/misc/spinner.svg'
										className='upload-spinner'
										alt='spinner'
									/>
								</div>
								:
								<>
									<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-margin'>
										{
											idFilePath ?
												<div className='dashboard-instructor-profile-section-sub-title'>
													<div>
														Id - <a
															href={`${DOCUMENT_FILE_PATH}${idFilePath}`}
															target='_blank'
														>Open</a>
													</div>
													<div>
														Update
														<input
															className='re-upload-button'
															type='file'
															id='uploadId'
															onChange={(event) => {
																if (event.target.files && event.target.files.length > 0) {
																	uploadId(event.target.files[0])
																}
															}}
															accept='image/*,.doc,.docx,.pdf'
															disabled={uploadingImage || uploadingDocument || updatingUserData}
															placeholder='Upload Id'
														/>
													</div>
												</div>
												:
												<>
													<div className='dashboard-instructor-profile-section-sub-title f-wrap'>
														Upload ID
													</div>
													<input
														type='file'
														id='uploadId'
														onChange={(event) => {
															if (event.target.files && event.target.files.length > 0) {
																uploadId(event.target.files[0])
															}
														}}
														accept='image/*,.doc,.docx,.pdf'
														disabled={uploadingImage || uploadingDocument || updatingUserData}
														placeholder='Upload Id'
													/>
													<div className='dashboard-warning-message-txt'>
														* Id Must be provided
													</div>
												</>
										}
									</div>
									<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-margin'>
										{
											certificateFilePath ?
												<div className='dashboard-instructor-profile-section-sub-title'>
													<div>
														Certificate Proof - <a
															href={`${DOCUMENT_FILE_PATH}${certificateFilePath}`}
															target='_blank'
														>Open</a>
													</div>
													<div>
														Update
														<input
															className='re-upload-button'
															type='file'
															id='uploadDoc'
															onChange={(event) => {
																if (event.target.files && event.target.files.length > 0) {
																	uploadDocument(event.target.files[0])
																}
															}}
															accept='image/*,.doc,.docx,.pdf'
															disabled={uploadingImage || uploadingDocument || updatingUserData}
															placeholder='Upload Document'
														/>
													</div>
												</div>
												:
												<>
													<div className='dashboard-instructor-profile-section-sub-title f-wrap'>
														Upload Highest Level Qualification Certificate
													</div>
													<input
														type='file'
														id='uploadDoc'
														onChange={(event) => {
															if (event.target.files && event.target.files.length > 0) {
																uploadDocument(event.target.files[0])
															}
														}}
														accept='image/*,.doc,.docx,.pdf'
														disabled={uploadingImage || uploadingDocument || updatingUserData}
														placeholder='Upload Document'
													/>
													<div className='dashboard-warning-message-txt'>
														* Qualification Must be provided
													</div>
												</>
										}
									</div>
								</>
							}
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Level'
									onChange={levelOnChange}
									value={level}
									options={['1','2','3','4']}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									blankValue='Level Not Set'
								/>
							</div>
							{/* {level && parseInt(level) > 0 && */}
								<div className='dashboard-instructor-profile-input-wrap'>
									<LabeledInput
										// label={`Price Level 1 (NZD) ${MIN_PRICE.toFixed(2)} - ${MAX_PRICE.toFixed(2)}`}
										label={`Price (NZD) ${MIN_PRICE.toFixed(2)} - ${MAX_PRICES[Number(level)].toFixed(2)}`}
										onChange={priceOnChange}
										value={price}
										onBlur={onPriceBlur}
										disabled={uploadingImage || uploadingDocument || updatingUserData}
									/>
								</div>
							{/* } */}
							{/* {level && parseInt(level) > 1 &&
								<div className='dashboard-instructor-profile-input-wrap'>
									<LabeledInput
										label={`Price Level 2 (NZD) ${MIN_PRICE.toFixed(2)} - ${MAX_PRICE_2.toFixed(2)}`}
										onChange={price2OnChange}
										value={price2}
										disabled={uploadingImage || uploadingDocument || updatingUserData}
									/>
								</div>
							}
							{level && parseInt(level) > 2 &&
								<div className='dashboard-instructor-profile-input-wrap'>
									<LabeledInput
										label={`Price Level 3 (NZD) ${MIN_PRICE.toFixed(2)} - ${MAX_PRICE_3.toFixed(2)}`}
										onChange={price3OnChange}
										value={price3}
										disabled={uploadingImage || uploadingDocument || updatingUserData}
									/>
								</div>
							}
							{level && parseInt(level) > 3 &&
								<div className='dashboard-instructor-profile-input-wrap'>
									<LabeledInput
										label={`Price Level 4 (NZD) ${MIN_PRICE.toFixed(2)} - ${MAX_PRICE_4.toFixed(2)}`}
										onChange={price4OnChange}
										value={price4}
										disabled={uploadingImage || uploadingDocument || updatingUserData}
									/>
								</div>
							} */}
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Languages'
									onChange={languageOnChange}
									value={undefined}
									options={LANGUAGES}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									keepBlank={true}
									blankValue='Add Language'
								/>
							</div>
							{!meetLanguageRequirements &&
								<div className='dashboard-warning-message-txt'>
									* You must provide a Language
								</div>
							}
							{languages && languages.length > 0 &&
								<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-list-wrap'>
									{languages.split(',').map((langaugeIndex: string, langIndex: number) =>
										<div
											className='dashboard-instructor-profile-list-value'
											key={`langValue${langIndex}`}
										>
											<img
												src='/imgs/icons/dashboard/icon-delete-x.svg'
												alt='remove language'
												className='dashboard-instructor-profile-list-value-remove-button'
												onClick={() => {
													languageOnChange(LANGUAGES[parseInt(langaugeIndex)])
												}}
											/>
											{LANGUAGES[parseInt(langaugeIndex)]}
										</div>
									)}
								</div>
							}
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Preferred Terrain'
									onChange={terrainsOnChange}
									value={undefined}
									options={TERRAINS}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									keepBlank={true}
									blankValue='Add Terrain'
								/>
							</div>
							{terrains && terrains.length > 0 &&
								<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-list-wrap'>
									{terrains.split(',').map((terrainIndex: string, listIndex: number) =>
										<div
											className='dashboard-instructor-profile-list-value'
											key={`terrainValue${listIndex}`}
										>
											<img
												src='/imgs/icons/dashboard/icon-delete-x.svg'
												alt='remove terrain'
												className='dashboard-instructor-profile-list-value-remove-button'
												onClick={() => {
													terrainsOnChange(TERRAINS[parseInt(terrainIndex)])
												}}
											/>
											{TERRAINS[parseInt(terrainIndex)]}
										</div>
									)}
								</div>
							}
							<div className='dashboard-instructor-profile-section-title'>
								Bank Details
							</div>
							{!meetBankRequirements &&
								<div className='dashboard-warning-message-txt'>
									* You must provide bank details
								</div>
							}
							<div className='dashboard-instructor-profile-input-wrap'>
								<LabeledDropDown
									label='Bank Country'
									onChange={bankCountryOnChange}
									value={bankCountry}
									options={BANK_COUNTRIES}
									disabled={uploadingImage || uploadingDocument || updatingUserData}
									keepBlank={true}
									blankValue='Choose Country'
								/>
							</div>
							{bankCountry &&
								<>
									<div className='dashboard-instructor-profile-input-wrap'>
										<LabeledInput
											label='Account Holder Name'
											onChange={bankNameOnChange}
											value={bankName}
											disabled={uploadingImage || uploadingDocument || updatingUserData}
											onBlur={onBankInputBlur}
										/>
									</div>
									<div className='dashboard-instructor-profile-input-wrap'>
										<LabeledInput
											label='Account Number'
											onChange={accountOnChange}
											value={account}
											// type='number'
											disabled={uploadingImage || uploadingDocument || updatingUserData}
											onBlur={onBankInputBlur}
										/>
									</div>
								</>
							}
							{bankCountry === 'UK' &&
								<>
									<div className='dashboard-instructor-profile-section-sub-title'>
										Sort Code
									</div>
									<div className='dashboard-instructor-profile-input-wrap dashboard-instructor-profile-input-wrap-triple'>
										<LabeledInput
											onChange={sortCode1Change}
											value={sortCode1}
											// type='number'
											disabled={uploadingImage || uploadingDocument || updatingUserData}
											onBlur={onBankInputBlur}
										/>
										<div className='dashboard-instructor-sort-code-seperator'>-</div>
										<LabeledInput
											onChange={sortCode2Change}
											value={sortCode2}
											// type='number'
											disabled={uploadingImage || uploadingDocument || updatingUserData}
											onBlur={onBankInputBlur}
										/>
										<div className='dashboard-instructor-sort-code-seperator'>-</div>
										<LabeledInput
											onChange={sortCode3Change}
											value={sortCode3}
											// type='number'
											disabled={uploadingImage || uploadingDocument || updatingUserData}
											onBlur={onBankInputBlur}
										/>
									</div>
								</>
							}
							{bankCountry === 'New Zealand' &&
								<div className='dashboard-instructor-profile-input-wrap'>
									<LabeledInput
										label='Swift/Bic'
										onChange={swiftBicOnChange}
										value={swiftBic}
										type='number'
										disabled={uploadingImage || uploadingDocument || updatingUserData}
										onBlur={onBankInputBlur}
									/>
								</div>
							}
						</div>
					</div>
					{valueChanged && 
						<>
							<div className='update-button-wrap-spacer' />
							<div className='update-button-wrap'>
								<CtaButton
									label='Update'
									onClick={() => updateDataOnClick()}
									size='medium'
									disabled={uploadingImage || uploadingDocument || updatingUserData}
								/>
							</div>
						</>
					}
				</>
			}
			{
			// showCropper &&
			imageSrc &&
				<div className='cropper-modal'>
					<div className='cropper-wrapper'>
						<Cropper
							image={imageSrc}
							crop={crop}
							rotation={rotation}
							zoom={zoom}
							aspect={1}
							onCropChange={setCrop}
							onRotationChange={setRotation}
							onCropComplete={onCropComplete}
							onZoomChange={setZoom}
						/>
					</div>
					<div className='cropped-button-wrap'>
						<CtaButton
							label='Cancel'
							size='medium'
							onClick={() => {
								setImageSrc(null)
							}}
						/>
						<CtaButton
							label='Crop Image'
							size='medium'
							onClick={() => {
								showCroppedImage()
							}}
						/>
					</div>
				</div>
			}
		</div>
	)
}

export default DashboardInstructorProfileContentArea
